import { Facet } from '../models/Facet';
import { IBackgroundTaskQueue } from '../models/backgroundTasks/IBackgroundTaskQueue';
import { IOfflineCache } from '../operations/offlineCache';
import { KeyFacet, Key } from '../models/KeyFacet';

export const backgroundTaskQueueFacet: Facet<IBackgroundTaskQueue> = new Facet('backgroundTaskQueue');

export const errorHandlerFacet: Facet<(error: Error) => void> = new Facet('errorHandler');

export const offlineCacheFacet: Facet<IOfflineCache> = new Facet('offlineCache');

export const configurationItemKeyFacet: KeyFacet<{}> = new KeyFacet('configuration', {});

export const configurationItemKey: Key<{}> = configurationItemKeyFacet.serialize({});

/**
 * Configuration facet to set the current default locale, as a fallback for data sources
 * which do not supply their own data locale.
 */
export const localeFacet: Facet<{
  language: string;
}> = new Facet('locale');

/**
 * Configuration facet to control cache pruning defaults.
 */
export const pruningConfigurationFacet: Facet<{
  pageDuration?: number;
  maxAge?: number;
}> = new Facet('pruningConfiguration');

/**
 * Configuration facet to get current login user information.
 */
export const loginUserFacet: Facet<{
  userLoginName: string;
  userEmail: string;
}> = new Facet('loginUser');

/**
 * Configuration facet to enable the File Hover Card feature in the Details and Tiles lists.
 * @public
 */
export const enableFileHoverCardConfigurationFacet = new Facet<boolean>('enableFileHoverCard');

/**
 * Configuration facet to enable the Shimmered Details List.
 * Default value is `true`.
 * @public
 */
export const enableShimmeredDetailsListConfigurationFacet = new Facet<boolean>('enableShimmeredDetailsList');

/**
 * Enable storage of larger backing items within [processResponse]
 * // TODO This needs to go in addon-sp
 */
export const enableSharePointBackingItemsFacet: Facet<boolean> = new Facet('enableSharePointBackingItems');

/**
 * Configuration facet to enable follow a Item feature in Details List.
 * @public
 */
export const enableFollowItemConfigurationFacet = new Facet<boolean>('enableFollowItem');

/**
 * Configuration facet to constrain the number of images loaded in parallel for items.
 * @beta
 */
export const maxParallelImageLoadCountFacet = new Facet<number>('maxParallelImageLoadCount');

/**
 * Controls whether or not to show item count for folders in the 'file size' column.
 */
export const enableItemCountInFileSizeColumnFacet = new Facet<boolean>('enableItemCountInFileSizeColumn');

/**
 * Configuration facet for enable persistent layout
 */
export const isItemsViewLayoutPersistenceEnableConfigurationFacet = new Facet<boolean>('enablePersistentLayout');
/**
 * Configuration facet for enabling Fluent Folder Cover layout
 */
export const enableFluentIconsFacet = new Facet<boolean>('enableFluentIcons');
/**
 * Configuration facet to enable trending analytics.
 * @public
 */
export const enableTrendingConfigurationFacet = new Facet<boolean>('enableTrending');
/**
 * Configuration facet to enable the read-only signal.
 * @public
 */
export const enableReadOnlyConfigurationFacet = new Facet<boolean>('enableReadOnly');
/**
 * Configuration facet to enable render debounce of item details rows.
 * @beta
 */
export const enableItemDetailsRowRenderDebounceFacet = new Facet<boolean>('enableItemDetailsRowRenderDebounce');
/**
 * Configuration facet to pre-computation and memoization of row and page data.
 * @beta
 */
export const enableDetailsListPreComputePagesFacet = new Facet<boolean>('enableDetailsListPreComputePages');
/**
 * Configuration facet to defer rendering of some field renderer components until after page load.
 * @beta
 */
export const enableDetailsListFieldRenderPageLoadBarrierFacet = new Facet<boolean>('enableDetailsListFieldRenderPageLoadBarrier');
/**
 * Whether or not to show a loading spinner in empty pages for list controls.
 */
export const enableListLoadingSpinnerFacet = new Facet<boolean>('enableListLoadingSpinner');
/**
 * Whether or not to use the 'Delete' and 'Cancel' confirmation strings for DeleteAction.
 */
export const enableDeleteCancelConfirmationStringsConfigurationFacet = new Facet<boolean>('enableDeleteCancelConfirmationStrings');
/**
 * Whether or not to use conflictUploadErrorTextWithNewVersion string and use 'Add file' as the replace action text
 * for upload action progress.
 */
export const enableNewConflictUploadErrorStringsConfigurationFacet = new Facet<boolean>('enableNewConflictUploadErrorStrings');
/**
 * Whether or not to allow "SkipViewportMeasure" flag for DetailsList render.
 */
export const disableSkipViewportMeasureConfigurationFacet = new Facet<boolean>('disableSkipViewportMeasure');
/**
 * Whether or not to disable shimmer before page load.
 */
export const disableShimmerBeforePageLoadConfigurationFacet = new Facet<boolean>('disableShimmerBeforePageLoad');
/**
 * Whether or not to enable usage of the React DnD library for drag and drop.
 */
export const enableReactDragDropConfigurationFacet = new Facet<boolean>('enableReactDragDrop');
/**
 * Enum for environmentConfiguration
 * @public
 */
export enum Environment {
  prod = 'prod',
  trailblazer = 'trailblazer',
  pathfinder = 'pathfinder',
  test = 'test'
}

// DO NOT add configuration facets specific to add-ons. Only configure features here which are defined in Items View Core.
